import { useState } from 'react';
import { useSetCustomerServiceSuspension } from '../api/customerManagerWorkspace.ts';
import { useT } from '../i18n/index.ts';
import { Button, Chip, Dialog, FormField, Panel, Textarea } from '../ui/index.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';

/**
 * Per-customer service kill switch (epic #1033 E4-C7, #1131). Admin + same-
 * brand sales lead only (gated by the caller); the gateway rejects suspended
 * accounts with SERVICE_SUSPENDED (403). Distinct from the admin-only account
 * lifecycle suspension (`AccountLifecycleCard`). Reversible both ways with a
 * confirmation dialog + reason captured into the audit log.
 */
export function ServiceSuspensionCard({
  orgId,
  customerEmail,
  serviceSuspended,
}: {
  readonly orgId: string;
  readonly customerEmail: string;
  readonly serviceSuspended: boolean;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const mutation = useSetCustomerServiceSuspension();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState('');

  // Toggling to the OPPOSITE of the current state.
  const nextSuspended = !serviceSuspended;

  function closeConfirm(): void {
    setConfirmOpen(false);
    setReason('');
  }

  function submit(): void {
    mutation.mutate(
      {
        orgId,
        body: {
          suspended: nextSuspended,
          ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success(
            t(
              nextSuspended
                ? 'customer_manager_360.service_suspension.toast.suspended'
                : 'customer_manager_360.service_suspension.toast.resumed',
            ),
          );
          closeConfirm();
        },
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return (
    <Panel
      caption={t('customer_manager_360.service_suspension.title')}
      testId="service-suspension-card"
      actions={
        <Chip tone={serviceSuspended ? 'bad' : 'ok'} testId="service-suspension-status">
          {t(
            serviceSuspended
              ? 'customer_manager_360.service_suspension.status.suspended'
              : 'customer_manager_360.service_suspension.status.active',
          )}
        </Chip>
      }
      padded
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-dense text-paper-low">
          {t('customer_manager_360.service_suspension.description')}
        </p>
        <div>
          <Button
            variant={nextSuspended ? 'danger' : 'primary'}
            onClick={() => setConfirmOpen(true)}
            data-testid="service-suspension-toggle"
          >
            {t(
              nextSuspended
                ? 'customer_manager_360.service_suspension.action.suspend'
                : 'customer_manager_360.service_suspension.action.resume',
            )}
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (!next) closeConfirm();
        }}
        title={t(
          nextSuspended
            ? 'customer_manager_360.service_suspension.confirm.title_suspend'
            : 'customer_manager_360.service_suspension.confirm.title_resume',
        )}
        description={customerEmail}
        closeLabel={t('common.close')}
        testId="service-suspension-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={closeConfirm} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant={nextSuspended ? 'danger' : 'primary'}
              loading={mutation.isPending}
              onClick={submit}
              data-testid="service-suspension-confirm"
            >
              {t(
                nextSuspended
                  ? 'customer_manager_360.service_suspension.action.suspend'
                  : 'customer_manager_360.service_suspension.action.resume',
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 break-words text-dense text-paper-low">
            {t(
              nextSuspended
                ? 'customer_manager_360.service_suspension.confirm.body_suspend'
                : 'customer_manager_360.service_suspension.confirm.body_resume',
            )}
          </p>
          <FormField
            label={t('customer_manager_360.service_suspension.reason_label')}
            fieldId="service-suspension-reason"
          >
            <Textarea
              id="service-suspension-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('customer_manager_360.service_suspension.reason_placeholder')}
              rows={3}
              data-testid="service-suspension-reason"
            />
          </FormField>
        </div>
      </Dialog>
    </Panel>
  );
}
